import { Anchor, Badge, Button, Divider, Group, Modal, SimpleGrid, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconArrowUpRight, IconLayoutColumns } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PresenceAvatars } from '@/components/presence/PresenceAvatars';
import type { UseResourcePresenceReturn } from '@/hooks/useResourcePresence';
import { presenceRowKey } from '@/lib/presence/presenceFhir';
import {
  UnifyTable,
  type UnifyTableAdvancedFilterSection,
  type UnifyTableColumn,
  type UnifyTableMultiFilterConfig,
} from '@/components/UnifyTable';
import { ColumnSelectModal } from '@/pages/episode-tracker/components/ColumnSelectModal';
import {
  MOCK_ENCOUNTERS,
  type ChargeItem,
  type EncounterRow,
  type EncounterStatus,
} from './mockEncounters';

const ENCOUNTERS_SCOPE = 'billing-encounters' as const;

/** Columns hidden by default; users can re-add them via the columns modal. */
const HIDDEN_BY_DEFAULT = new Set(['provider', 'className', 'serviceProvider', 'reason']);

const STATUS_COLORS: Record<EncounterStatus, string> = {
  planned: 'gray',
  arrived: 'blue',
  triaged: 'indigo',
  'in-progress': 'cyan',
  onleave: 'yellow',
  finished: 'green',
  cancelled: 'red',
};

function statusLabel(status: EncounterStatus): string {
  return status
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}


function formatDateTime(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Unique, sorted values for a row field, as FilterMultiSelect options. */
function optionsFor(rows: EncounterRow[], select: (row: EncounterRow) => string): string[] {
  return [...new Set(rows.map(select).filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

/** One label/value pair in the encounter detail modal. */
function DetailItem({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text size="sm">{value || '—'}</Text>
    </Stack>
  );
}

/** Charge items (CPT line items) for an encounter, shown at the bottom of the modal. */
function ChargeItemList({ items }: { items: ChargeItem[] }): JSX.Element {
  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        Charge Items
      </Text>
      {items.length === 0 ? (
        <Text size="sm" c="dimmed">
          No charge items
        </Text>
      ) : (
        <Stack gap="xs">
          {items.map((item, index) => (
            <Group key={`${item.code}-${index}`} justify="space-between" wrap="nowrap" gap="sm">
              <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                <Text size="sm" fw={600}>
                  {item.code}
                </Text>
                <Text size="sm" c="dimmed" truncate>
                  {item.description}
                </Text>
              </Group>
              <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                {item.quantity} {item.quantity === 1 ? 'unit' : 'units'}
              </Text>
            </Group>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

export interface EncountersTableProps {
  presenceByRow?: UseResourcePresenceReturn['presenceByRow'];
  setActiveRow?: UseResourcePresenceReturn['setActiveRow'];
}

export function EncountersTable({ presenceByRow, setActiveRow }: EncountersTableProps): JSX.Element {
  const data = MOCK_ENCOUNTERS;
  const [opened, { open, close }] = useDisclosure(false);
  const [selected, setSelected] = useState<EncounterRow | null>(null);
  const [columnModalOpen, { open: openColumnModal, close: closeColumnModal }] =
    useDisclosure(false);
  const [visibleColumnOrder, setVisibleColumnOrder] = useState<string[] | null>(null);

  const handleRowClick = (row: EncounterRow): void => {
    setSelected(row);
    open();
  };

  useEffect(() => {
    setActiveRow?.(
      opened && selected
        ? {
            rowId: selected.id,
            scope: ENCOUNTERS_SCOPE,
            subjectResourceType: 'Encounter',
          }
        : null,
    );
  }, [opened, selected, setActiveRow]);

  const columns = useMemo(
    (): UnifyTableColumn<EncounterRow>[] => [
      {
        key: 'identifier',
        label: 'Encounter ID',
        render: (row) => <Text size="xs" c="dimmed">{row.identifier}</Text>,
        getValue: (row) => row.identifier,
      },
      {
        key: 'start',
        label: 'Start',
        render: (row) => <Text size="sm">{formatDateTime(row.start)}</Text>,
        getValue: (row) => row.start,
      },
      {
        key: 'status',
        label: 'Status',
        render: (row) => (
          <Badge variant="light" size="sm" color={STATUS_COLORS[row.status]}>
            {statusLabel(row.status)}
          </Badge>
        ),
        getValue: (row) => statusLabel(row.status),
      },
      {
        key: 'claimStatus',
        label: 'Claim Status',
        render: () => <Text size="sm" c="dimmed">—</Text>,
        getValue: () => '',
      },
      {
        key: 'patientName',
        label: 'Client',
        render: (row) => <Text size="sm" fw={600}>{row.patientName}</Text>,
        getValue: (row) => row.patientName,
      },
      {
        key: 'type',
        label: 'Type',
        getValue: (row) => row.type,
      },
      {
        key: 'cptCode',
        label: 'Primary CPT',
        getValue: (row) => row.cptCode,
      },
      {
        key: 'lengthMinutes',
        label: 'Length',
        align: 'right',
        render: (row) => <Text size="sm">{row.lengthMinutes} min</Text>,
        getValue: (row) => String(row.lengthMinutes).padStart(4, '0'),
      },
      {
        key: 'chargeItemCount',
        label: 'Charge Items',
        align: 'right',
        render: (row) => <Text size="sm">{row.chargeItems.length}</Text>,
        getValue: (row) => String(row.chargeItems.length).padStart(4, '0'),
      },
      {
        key: 'episodeOfCare',
        label: 'Episode of Care',
        getValue: (row) => row.episodeOfCare,
      },
      {
        key: 'provider',
        label: 'Provider',
        getValue: (row) => row.provider,
      },
      {
        key: 'className',
        label: 'Class',
        getValue: (row) => row.className,
      },
      {
        key: 'reason',
        label: 'Reason',
        render: (row) => <Text size="sm" c={row.reason ? undefined : 'dimmed'}>{row.reason || '—'}</Text>,
        getValue: (row) => row.reason,
      },
      {
        key: 'serviceProvider',
        label: 'Facility',
        getValue: (row) => row.serviceProvider,
      },
      {
        key: 'actions',
        label: '',
        sortable: false,
        align: 'right',
        render: (row) => (
          <Group
            gap="xs"
            justify="flex-end"
            wrap="nowrap"
            onClick={(e) => e.stopPropagation()}
          >
            <PresenceAvatars
              actors={presenceByRow?.get(presenceRowKey(ENCOUNTERS_SCOPE, row.id)) ?? []}
            />
          </Group>
        ),
        getValue: () => '',
      },
    ],
    [presenceByRow],
  );

  const columnOptions = useMemo(
    () =>
      columns
        .filter((c) => c.key !== 'actions')
        .map((c) => ({ key: c.key, displayLabel: c.label })),
    [columns],
  );

  const defaultColumnKeys = useMemo(
    () =>
      columns
        .map((c) => c.key)
        .filter((key) => key !== 'actions' && !HIDDEN_BY_DEFAULT.has(key)),
    [columns],
  );

  const visibleColumnKeys = useMemo(
    () => [...(visibleColumnOrder ?? defaultColumnKeys), 'actions'],
    [visibleColumnOrder, defaultColumnKeys],
  );

  const multiFilters = useMemo(
    (): UnifyTableMultiFilterConfig[] => [
      {
        columnKey: 'status',
        allLabel: 'All statuses',
        placeholder: 'Status',
        data: ['Finished', 'Planned', 'Cancelled'],
        defaultValue: ['Finished'],
      },
      {
        columnKey: 'provider',
        allLabel: 'All providers',
        placeholder: 'Provider',
        data: optionsFor(data, (r) => r.provider),
        emptyMeansAll: true,
      },
    ],
    [data],
  );

  const advancedFilterSections = useMemo(
    (): UnifyTableAdvancedFilterSection[] => [
      {
        label: 'Class',
        columnKey: 'className',
        options: [
          { value: 'In Person', label: 'In Person' },
          { value: 'Telehealth', label: 'Telehealth' },
        ],
      },
    ],
    [],
  );

  return (
    <>
      <UnifyTable
        title="Encounters"
        subtitle={`${data.length} encounters`}
        columns={columns}
        visibleColumnKeys={visibleColumnKeys}
        data={data}
        getRowKey={(row) => row.id}
        searchable
        searchPlaceholder="Search by client, provider, reason, CPT…"
        sortable
        pagination
        defaultRowsPerPage={10}
        multiFilters={multiFilters}
        advancedFilterSections={advancedFilterSections}
        advancedFiltersInHeader
        onRowClick={handleRowClick}
        headerRight={
          <Button
            variant="outline"
            leftSection={<IconLayoutColumns size={16} />}
            onClick={openColumnModal}
          >
            Columns
          </Button>
        }
      />

      <ColumnSelectModal
        opened={columnModalOpen}
        onClose={closeColumnModal}
        allColumns={columnOptions}
        defaultColumnKeys={defaultColumnKeys}
        value={visibleColumnOrder}
        onApply={setVisibleColumnOrder}
      />

      <Modal
        opened={opened}
        onClose={close}
        title={
          <Group gap="sm">
            <Text fw={600}>{selected?.patientName}</Text>
            {selected && (
              <Badge variant="light" size="sm" color={STATUS_COLORS[selected.status]}>
                {statusLabel(selected.status)}
              </Badge>
            )}
          </Group>
        }
        size="lg"
        centered
      >
        {selected && (
          <Stack gap="md">
            <SimpleGrid cols={2} spacing="md">
              <DetailItem label="Provider" value={selected.provider} />
              <DetailItem label="Facility" value={selected.serviceProvider} />
              <DetailItem label="Class" value={selected.className} />
              <DetailItem label="Type" value={selected.type} />
              <DetailItem label="Length" value={`${selected.lengthMinutes} min`} />
              <DetailItem label="Start" value={formatDateTime(selected.start)} />
              <DetailItem label="End" value={formatDateTime(selected.end)} />
              <DetailItem label="Episode of Care" value={selected.episodeOfCare} />
            </SimpleGrid>
            <Divider />
            <ChargeItemList items={selected.chargeItems} />
            <Divider />
            <Group justify="space-between" gap="sm">
              <Text size="xs" c="dimmed">
                Encounter ID: {selected.identifier}
              </Text>
              <Group gap="lg" wrap="nowrap">
                <Anchor component="button" type="button" size="sm" fw={600}>
                  <Group gap={4} wrap="nowrap">
                    View claim
                    <IconArrowUpRight size={16} />
                  </Group>
                </Anchor>
                <Anchor
                  component={Link}
                  to={`/dashboard/appointments/${selected.appointmentId}`}
                  size="sm"
                  fw={600}
                >
                  <Group gap={4} wrap="nowrap">
                    View appointment
                    <IconArrowUpRight size={16} />
                  </Group>
                </Anchor>
              </Group>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
